import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { DashboardSummary } from '../../core/api/models/dashboard.model';
import { FunctionSource } from '../../core/api/models/function.model';
import { FunctionQueueStatus, FunctionRun } from '../../core/api/models/function-run.model';
import { DashboardApiService } from '../../core/api/services/dashboard-api.service';
import { getLocalDayUtcRange } from '../../core/date/local-day-range';
import { formatRunDuration } from '../../shared/ui/duration/duration';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-dashboard.component.scss',
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  private readonly api = inject(DashboardApiService);
  private refreshTimer?: number;

  summary: DashboardSummary | null = null;
  isLoading = false;
  loadError = '';
  lastUpdated: Date | null = null;
  sourceFilter: FunctionSource | '' = '';
  functionFilter = '';
  statusFilter: FunctionQueueStatus | '' = '';

  readonly activeStatuses: FunctionQueueStatus[] = ['Pending', 'Running', 'Retrying'];
  ngOnInit(): void {
    this.loadDashboard();
    // this.refreshTimer = window.setInterval(
    //   () => this.loadDashboard(false),
    //   environment.refreshIntervals.dashboardMilliseconds);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer !== undefined) {
      window.clearInterval(this.refreshTimer);
    }
  }

  loadDashboard(showLoading = true): void {
    const range = getLocalDayUtcRange();
    if (showLoading) {
      this.isLoading = true;
    }
    this.loadError = '';

    const request = this.api.getDashboardSummary(range.fromUtc, range.toUtc, Date.now());
    request.pipe(finalize(() => this.isLoading = false)).subscribe({
      next: (result) => {
        this.summary = result;
        this.lastUpdated = new Date();
      },
      error: () => this.loadError = 'Dashboard data could not be loaded. Check the API connection and try again.',
    });
  }

  get filteredActiveRuns(): FunctionRun[] {
    const activeRuns = this.summary?.activeRuns ?? [];
    const result = activeRuns.filter((run) =>
      (!this.sourceFilter || run.source === this.sourceFilter)
      && (!this.functionFilter || run.functionName === this.functionFilter)
      && (!this.statusFilter || run.status === this.statusFilter));
    return result;
  }

  get activeSources(): FunctionSource[] {
    const values = (this.summary?.activeRuns ?? []).map((run) => run.source);
    const result = [...new Set(values)].sort();
    return result;
  }

  get activeFunctions(): FunctionRun[] {
    const functions = new Map<string, FunctionRun>();
    (this.summary?.activeRuns ?? []).forEach((run) => functions.set(run.functionName, run));
    const result = [...functions.values()].sort((left, right) => left.functionName.localeCompare(right.functionName));
    return result;
  }

  duration(run: FunctionRun): string {
    return formatRunDuration(run.startedAtUtc, run.completedAtUtc);
  }

  statusClass(status?: FunctionQueueStatus | null): string {
    const result = `status-${(status ?? 'unknown').toLowerCase()}`;
    return result;
  }
}
